import jsPDF from "jspdf";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  showErrorAlert,
  showSuccessAlert,
  showSuccessEditAlert,
} from "../../components/alerts";
import medicGeekApi from "../../config/axiosConfig";
import ProRichTextEditor from "../../contexts/ProRichTextEditor";
import TipTap from "../../styles/TipTap.css";

const FacturaForm = ({ currentForm }) => {
  const [facturas, setFacturas] = useState([]);
  const [showForm] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const facturasPerPage = 10;
  const [selectedFacturaLocal, setSelectedFacturaLocal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPacienteModalOpen, setIsPacienteModalOpen] = useState(false);
  const [pacientes, setPacientes] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [monto, setMonto] = useState("");
  const [displayedMonto, setDisplayedMonto] = useState(""); // Guardar el monto formateado para la visualización
  const [profileData, setProfileData] = useState(null);
  const searchInputRef = useRef(null);
  // Skeleton
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Contador de caracteres
  const maxChars3 = 1000;

  const [formulario, setFormulario] = useState({
    nombrePaciente: "",
    detalleFactura: "",
    conceptoFacturas: "",
    montoFactura: "",
  });

  const openModal = (factura) => {
    if (factura) {
      setSelectedFacturaLocal(factura);
      setIsEditing(true); // Configura el modo de edición si hay una factura
      setFormulario({
        pcteId: factura.pcteId || "",
        detalleFactura: factura.factDetails || "",
        fechaFactura: factura.fatFec || "",
        conceptoFacturas: factura.conceptoId || "",
        montoFactura: factura.facturaCt || "",
      });
    } else {
      setIsEditing(false); // En modo de creación si no hay factura seleccionada
      setFormulario({
        pcteId: "",
        nombrePaciente: "",
        detalleFactura: "",
        conceptoFacturas: "",
        montoFactura: "",
      });
    }

    setModalOpen(true); // Abre el modal al final
  };

  const closeModal = () => {
    setSelectedFacturaLocal(null); // Limpia el factura seleccionado
    setIsEditing(false); // Resetear el modo edición cuando se cierra el modal
    setModalOpen(false);
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNumber(0);
  };

  useEffect(() => {
    const fetchDataAndPaciente = async () => {
      await fetchData();
      await fetchProfile();

      if (selectedFacturaLocal && formulario.pcteId) {
        const paciente = await buscarPacientePorId(formulario.pcteId);
        // console.log('Estas editando el paciente', !!paciente);
      }
    };

    fetchDataAndPaciente();
  }, [searchTerm, formulario.pcteId, selectedFacturaLocal]);

  useEffect(() => {
    if (selectedFacturaLocal) {
      setFormulario({
        nombrePaciente: selectedFacturaLocal.pcteId || "",
        detalleFactura: selectedFacturaLocal.factDetails || "",
        fechaFactura: selectedFacturaLocal.fatFec || "",
        conceptoId: selectedFacturaLocal.conceptoId || "",
        montoFactura: selectedFacturaLocal.facturaCt || "",
      });
    }
  }, [selectedFacturaLocal]);

  const fetchData = async () => {
    setLoading(true); // Iniciar la carga
    try {
      // Obtener facturas
      const response = await medicGeekApi.get(
        `/facturas/detail?search=${searchTerm}`,
        {
          params: {
            $or: [
              { facturaNm: { $regex: searchTerm, $options: "i" } },
              { facturaDesc: { $regex: searchTerm, $options: "i" } },
            ],
          },
        }
      );
      const sortedFacturas = response.data.sort((a, b) => a.factId - b.factId);

      // Obtener pacientes
      const patientsResponse = await medicGeekApi.get(
        `/patients?search=${searchTerm}`,
        {
          params: {
            $or: [
              { pcteNom: { $regex: searchTerm, $options: "i" } },
              { pcteNom2: { $regex: searchTerm, $options: "i" } },
              { pcteApe1: { $regex: searchTerm, $options: "i" } },
            ],
          },
        }
      );
      const sortedPacientes = patientsResponse.data.sort(
        (a, b) => a.pcteId - b.pcteId
      );

      // Obtener conceptos
      const conceptoResponse = await medicGeekApi.get(
        `/conceptos?search=${searchTerm}`,
        {
          params: {
            $or: [
              { conceptoNm: { $regex: searchTerm, $options: "i" } },
              { conceptoDesc: { $regex: searchTerm, $options: "i" } },
            ],
          },
        }
      );
      const sortedConceptos = conceptoResponse.data.sort(
        (a, b) => a.conceptoId - b.conceptoId
      );

      setFacturas(sortedFacturas);
      setPacientes(sortedPacientes);
      setConceptos(sortedConceptos);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false); // Finalizar carga
    }
  };

  const fetchProfile = async () => {
    try {
      // Obtener el token desde localStorage
      const token = localStorage.getItem("token");
      if (token) {
        // Decodificar el token para obtener el nombre de usuario
        const decodedToken = jwtDecode(token);
        const username = decodedToken.username; // Asegúrate de que 'username' sea correcto

        if (username) {
          // Buscar perfil por nombre de usuario
          const response = await medicGeekApi.get(`/profile/user/${username}`);
          const data = response.data;

          // Verificar si se recibió el perfil
          if (data) {
            setProfileData(data);
            // setIsEditing(true);
          } else {
            console.error("No se encontró el perfil del usuario.");
          }
        } else {
          console.error("No se pudo obtener el nombre de usuario del token.");
        }
      } else {
        console.error("No se encontró el token.");
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const pageCount = Math.ceil(facturas.length / facturasPerPage);

  const handlePageChange = ({ selected }) => {
    setPageNumber(selected);
  };

  function parseDate(dateString) {
    if (!dateString) return 0; // Retorna 0 si la fecha está vacía

    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("/").map(Number);
    const [time, modifier] = timePart.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours < 12) {
      hours += 12;
    } else if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    const date = new Date(year, month - 1, day, hours, minutes);
    return date.getTime();
  }

  const displayedFacturas = facturas
    .filter((factura) => {
      const factId = factura.factId
        ? factura.factId.toString().toLowerCase()
        : "";
      const factDetails = factura.factDetails
        ? factura.factDetails.toLowerCase()
        : "";

      return (
        factId.includes(searchTerm.toLowerCase()) ||
        factDetails.includes(searchTerm.toLowerCase())
      );
    })
    .slice() // Crear una copia del array para evitar mutaciones
    .sort((a, b) => parseDate(b.factFec) - parseDate(a.factFec)) // Ordenar de manera descendente por fecha
    .slice(pageNumber * facturasPerPage, (pageNumber + 1) * facturasPerPage); // Aplicar paginación

  const updateFacturaForm = () => {
    medicGeekApi
      .get("/facturas")
      .then((response) => {
        setFacturas(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const openPacienteModal = () => {
    setIsPacienteModalOpen(true);
    // Forzar enfoque en el input aquí, después de abrir el modal
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const closePacienteModal = () => {
    setIsPacienteModalOpen(false);
  };

  const handleModalSearchTermChange = (e) => {
    setModalSearchTerm(e.target.value);
    setModalCurrentPage(1);
  };

  const filteredPacientes = pacientes.filter((paciente) => {
    // Separar los términos de búsqueda por espacio y eliminar espacios vacíos
    const searchWords = modalSearchTerm.toLowerCase().trim().split(" ");

    // Combinar nombre completo del paciente
    const fullName = [
      paciente.pcteNom,
      paciente.pcteNom2,
      paciente.pcteApe1,
      paciente.pcteApe2,
    ]
      .filter(Boolean) // Filtrar los valores nulos o undefined
      .map((name) => name.toLowerCase()) // Convertir cada nombre a minúsculas
      .join(" "); // Unir en una cadena completa

    // Verificar si todos los términos de búsqueda coinciden con el nombre completo
    return searchWords.every((word) => fullName.includes(word));
  });

  const totalPages = Math.ceil(filteredPacientes.length / itemsPerPage);

  const handlePacienteSelection = (paciente) => {
    if (paciente) {
      setFormulario({
        ...formulario,
        pcteId: paciente.pcteId || "",
        nombreDelPaciente: `${paciente.pcteNom || ""} ${
          paciente.pcteNom2 || ""
        } ${paciente.pcteApe1 || ""} ${paciente.pcteApe2 || ""}`,
        direccionPaciente: paciente.pcteDir || "",
        contactoPaciente: paciente.pcteTel || paciente.pcteCel,
        emailPaciente: paciente.pcteEmail || "",
      });

      setPacienteSeleccionado(paciente);
      setModalOpen(true);
      closePacienteModal();
    } else {
      console.error("El paciente seleccionado es nulo.");
    }
  };

  const buscarPacientePorId = async (pcteId) => {
    try {
      // Hacer la solicitud al endpoint de pacientes para obtener el paciente por ID
      const response = await medicGeekApi.get(`/patients/${pcteId}`);

      const paciente = response.data;

      // Verificar si se encontró un paciente y actualizar el formulario
      if (paciente) {
        setFormulario((formulario) => ({
          ...formulario,
          pcteId: paciente.pcteId,
          nombreDelPaciente: `${paciente.pcteNom || ""} ${
            paciente.pcteNom2 || ""
          } ${paciente.pcteApe1 || ""} ${paciente.pcteApe2 || ""}`,
          direccionPaciente: paciente.pcteDir || "",
          contactoPaciente: paciente.pcteTel || paciente.pcteCel || "",
          emailPaciente: paciente.pcteEmail || "",
        }));
      } else {
        console.error("No se encontró un paciente con el ID proporcionado.");
      }
    } catch (error) {
      console.error("Error al buscar el paciente:", error);
    }
  };

  const handleSelectChange = (e) => {
    const conceptoSeleccionado = e.target.value;

    setFormulario({
      ...formulario,
      conceptoFacturas: conceptoSeleccionado,
    });
  };

  const getConceptoNombre = (conceptoId) => {
    const concepto = conceptos.find(
      (c) => Number(c.conceptoId) === Number(conceptoId)
    );
    return concepto ? concepto.conceptoNm : "Concepto no disponible";
  };

  const SkeletonTableRow = () => (
    <tr>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "150px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "70px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "200px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}
        ></div>
      </td>
    </tr>
  );

  const formatNumber = (value) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: "DOP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/RD/g, "").replace(/,/g, ""); // Eliminar el prefijo RD y comas

    if (!isNaN(rawValue) || rawValue === "") {
      const numericValue = rawValue === "" ? "" : Number(rawValue); // Convertir a número

      // Actualizar el estado del formulario con el monto plano para almacenamiento
      setFormulario({
        ...formulario,
        montoFactura: numericValue, // Guardar como número plano
      });

      // Actualizar la visualización si necesitas mostrar el monto formateado
      const formattedValue = rawValue === "" ? "" : formatNumber(numericValue);
      setDisplayedMonto(formattedValue); // Esto asume que tienes un estado separado para la visualización
    }
  };

  const handleFocus = (event) => {
    event.target.select(); // Selecciona todo el texto del input
  };

  const normalizarMonto = (valor) => {
    const valorLimpio = String(valor ?? "")
      .replace(/,/g, "")
      .trim();

    const numero = Number(valorLimpio);

    return valorLimpio === "" || Number.isNaN(numero)
      ? 0
      : numero;
  };

  const guardarNuevoFactura = () => {
    const fechaActual = new Date();

    const dia = String(fechaActual.getDate()).padStart(2, "0");
    const mes = String(fechaActual.getMonth() + 1).padStart(2, "0");
    const año = fechaActual.getFullYear();

    const ampm = fechaActual.getHours() >= 12 ? "PM" : "AM";

    let horas = fechaActual.getHours() % 12;
    horas = horas === 0 ? 12 : horas;

    const minutos = String(
      fechaActual.getMinutes()
    ).padStart(2, "0");

    const fechaHoraFormateada =
      `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;


    // ==========================================
    // NORMALIZAR MONTO
    // ==========================================

    const montoFactura = normalizarMonto(
      formulario.montoFactura
    );


    const nuevoFactura = {
      pcteId: formulario.pcteId || null,

      factDetails:
        formulario.detalleFactura || "",

      factFec: fechaHoraFormateada,

      conceptoId:
        formulario.conceptoId || null,

      // Si está vacío, inválido o NaN -> 0
      facturaCt: montoFactura,
    };


    console.log(
      "Factura enviada:",
      nuevoFactura
    );


    medicGeekApi
      .post(
        "/facturas",
        nuevoFactura
      )
      .then((response) => {
        showSuccessAlert();
        closeModal();
        fetchData();
      })
      .catch((error) => {
        console.error(
          "Error al crear la factura",
          error
        );
      });
  };

  const editarFactura = () => {
    if (
      selectedFacturaLocal &&
      selectedFacturaLocal.factId
    ) {

      const fechaActual = new Date();

      const dia = String(
        fechaActual.getDate()
      ).padStart(2, "0");

      const mes = String(
        fechaActual.getMonth() + 1
      ).padStart(2, "0");

      const año =
        fechaActual.getFullYear();

      const ampm =
        fechaActual.getHours() >= 12
          ? "PM"
          : "AM";

      let horas =
        fechaActual.getHours() % 12;

      horas =
        horas === 0
          ? 12
          : horas;

      const minutos = String(
        fechaActual.getMinutes()
      ).padStart(2, "0");

      const fechaHoraFormateada =
        `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;


      // ==========================================
      // NORMALIZAR MONTO
      // ==========================================

      const montoFactura = normalizarMonto(
        formulario.montoFactura
      );


      const facturaEditada = {
        pcteId:
          formulario.pcteId || null,

        factDetails:
          formulario.detalleFactura || "",

        factFec:
          fechaHoraFormateada,

        conceptoId:
          formulario.conceptoId || null,

        // Si está vacío, inválido o NaN -> 0
        facturaCt:
          montoFactura,
      };


      console.log(
        "Factura editada:",
        facturaEditada
      );


      medicGeekApi
        .patch(
          `/facturas/${selectedFacturaLocal.factId}`,
          facturaEditada
        )
        .then((response) => {
          showSuccessEditAlert();
          closeModal();
          fetchData();
        })
        .catch((error) => {
          console.error(
            "Error al editar la factura",
            error
          );
        });

    } else {

      showErrorAlert(
        "No se puede editar: factura no válida."
      );
    }
  };

  const numeroALetras = (num) => {
    const unidades = [
      "",
      "UNO",
      "DOS",
      "TRES",
      "CUATRO",
      "CINCO",
      "SEIS",
      "SIETE",
      "OCHO",
      "NUEVE",
    ];
    const especiales = [
      "DIEZ",
      "ONCE",
      "DOCE",
      "TRECE",
      "CATORCE",
      "QUINCE",
      "DIECISEIS",
      "DIECISIETE",
      "DIECIOCHO",
      "DIECINUEVE",
    ];
    const decenas = [
      "",
      "",
      "VEINTE",
      "TREINTA",
      "CUARENTA",
      "CINCUENTA",
      "SESENTA",
      "SETENTA",
      "OCHENTA",
      "NOVENTA",
    ];
    const centenas = [
      "",
      "CIENTO",
      "DOSCIENTOS",
      "TRESCIENTOS",
      "CUATROCIENTOS",
      "QUINIENTOS",
      "SEISCIENTOS",
      "SETECIENTOS",
      "OCHOCIENTOS",
      "NOVECIENTOS",
    ];

    if (num === 0) return "CERO";

    let letras = "";

    // Miles
    if (Math.floor(num / 1000) > 0) {
      if (Math.floor(num / 1000) === 1) {
        letras += "MIL ";
      } else {
        letras += numeroALetras(Math.floor(num / 1000)) + " MIL ";
      }
      num %= 1000;
    }

    // Centenas
    if (Math.floor(num / 100) > 0) {
      letras += centenas[Math.floor(num / 100)] + " ";
      num %= 100;
    }

    // Decenas y unidades especiales (10-19)
    if (num >= 10 && num < 20) {
      letras += especiales[num - 10] + " ";
    } else {
      // Decenas
      if (Math.floor(num / 10) > 1) {
        letras += decenas[Math.floor(num / 10)];
        if (num % 10 > 0) letras += " Y ";
      }

      // Unidades
      letras += unidades[num % 10] + " ";
    }

    return letras.trim();
  };

  const convertirFacturaCtALetras = (valor) => {
    if (valor === undefined || isNaN(valor)) return "CERO PESOS";

    const [parteEntera, decimales] = parseFloat(valor).toFixed(2).split(".");
    const cantidadEnLetras = numeroALetras(parseInt(parteEntera, 10));
    const centavosEnLetras =
      decimales === "00"
        ? ""
        : `CON ${numeroALetras(parseInt(decimales, 10))} CENTAVOS`;

    return `${cantidadEnLetras} PESOS ${centavosEnLetras}`.trim();
  };

  const previsualizarPDF = async (factura) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const additionalMargin = 40;
    const fontSize = 10;

    doc.setFontSize(fontSize);

    const fechaLarga = new Date().toLocaleDateString();
    const cantidadEnLetras = convertirFacturaCtALetras(factura.facturaCt);
    const nombreCompleto = factura.patient
      ? `${factura.patient.pcteNom || ""} ${factura.patient.pcteNom2 || ""} ${
          factura.patient.pcteApe1 || ""
        } ${factura.patient.pcteApe2 || ""}`
      : "OTROS INGRESOS";

    // Título
    doc.setFontSize(22);
    doc.setFont(undefined, "bold");
    const detalleText = "RECIBO DE PAGO";
    const textWidth = doc.getTextWidth(detalleText);
    const centeredX = (pageWidth - textWidth) / 2;
    doc.text(detalleText, centeredX, margin + additionalMargin);
    doc.setFont(undefined, "normal");

    // Información del paciente y recibo
    doc.setFontSize(12);
    const profileText = `Recibimos en el día de hoy de parte de ${nombreCompleto}. Cédula de identidad número ${
      factura.patient?.pcteNumDoc || ""
    }, la suma de ${parseFloat(factura.facturaCt).toLocaleString("es-DO", {
      style: "currency",
      currency: "DOP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} (${cantidadEnLetras}), por concepto de ${getConceptoNombre(
      factura.conceptoId
    )} realizada en el día de hoy ${factura.factFec}.`;

    const profileLines = doc.splitTextToSize(
      profileText,
      pageWidth - 2 * margin
    );
    let y = margin + additionalMargin + 20;
    const lineHeight = fontSize * 0.6;

    profileLines.forEach((line) => {
      if (
        y + lineHeight >
        pageHeight - margin - additionalMargin - lineHeight
      ) {
        doc.addPage();
        y = margin + additionalMargin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    // Nombre y exequátur del doctor
    const alturaNombre = y + 70;
    const lineaY = alturaNombre - 5;
    const lineaX = (pageWidth - 80) / 2;
    doc.setLineWidth(0.5);
    doc.line(lineaX, lineaY, lineaX + 80, lineaY);

    const nombreDoctor = profileData.docNom || "Doctor";
    doc.setFontSize(12);
    const nombreX = (pageWidth - doc.getTextWidth(nombreDoctor)) / 1.41;
    doc.text(nombreDoctor, nombreX, alturaNombre, { align: "center" });

    const exequaturText = `Exequátur: ${profileData.docExe || "N/A"}`;
    doc.text(exequaturText, nombreX, alturaNombre + 5, { align: "center" });

    // Convertir el PDF a Blob y generar la vista previa/imprimir
    const pdfBlob = doc.output("blob");
    let iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const blobUrl = URL.createObjectURL(pdfBlob);
    iframe.src = blobUrl;

    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      iframe.contentWindow.onafterprint = () => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      };
    };
  };

  const getPlainTextLength = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").length;
  };


 return (
  <div className="pro-container">

    {/* ===================================================== */}
    {/* HEADER */}
    {/* ===================================================== */}

    <div className="pro-header">

      <div className="pro-header-left">

        <h2 className="pro-title">
          Facturación
        </h2>

        <p className="pro-subtitle">
          Gestión y mantenimiento de facturas
        </p>

      </div>

      <div className="pro-actions">

        <div className="pro-search">

          <i className="bi bi-search"></i>

          <input
            type="search"
            placeholder="Buscar factura..."
            value={searchTerm}
            onChange={handleSearchTermChange}
          />

        </div>

        <button
          type="button"
          className="pro-btn-primary"
          onClick={() => openModal(null)}
        >

          <i className="bi bi-plus-circle"></i>

          Nueva Factura

        </button>

      </div>

    </div>

    {/* ===================================================== */}
    {/* TABLE DESKTOP */}
    {/* ===================================================== */}

    <div className="pro-card d-none d-md-block">

      <div className="table-responsive">

        <table className="table pro-table">

          <thead>

            <tr>
              <th style={{ width: "5%" }}>ID</th>
              <th style={{ width: "25%" }}>
                Tipo de Ingreso
              </th>
              <th style={{ width: "25%" }}>
                Concepto
              </th>
              <th style={{ width: "15%" }}>
                Fecha
              </th>
              <th style={{ width: "15%" }}>
                Monto
              </th>
              <th style={{ width: "15%" }}>
                Acciones
              </th>
            </tr>

          </thead>

          <tbody>

            {loading ? (

              Array.from({ length: 15 }).map((_, index) => (
                <SkeletonTableRow key={index} />
              ))

            ) : (

              displayedFacturas.map((factura) => (

                <tr key={factura.factId}>

                  <td>
                    #{factura.factId}
                  </td>

                  <td className="fw-semibold">

                    {factura.patient
                      ? `${factura.patient.pcteNom || ""} ${
                          factura.patient.pcteNom2 || ""
                        } ${factura.patient.pcteApe1 || ""} ${
                          factura.patient.pcteApe2 || ""
                        }`
                      : "OTROS INGRESOS"}

                  </td>

                  <td>

                    {getConceptoNombre(factura.conceptoId)}

                  </td>

                  <td>

                    {factura.factFec || "No disponible"}

                  </td>

                  <td className="fw-bold text-success">

                    {parseFloat(
                      factura.facturaCt
                    ).toLocaleString("es-DO", {
                      style: "currency",
                      currency: "DOP",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}

                  </td>

                  <td>

                    <div className="pro-table-actions">

                      <button
                        type="button"
                        className="pro-action-btn primary"
                        title="Editar"
                        onClick={() => openModal(factura)}
                      >

                        <i className="bi bi-pencil-square"></i>

                      </button>

                      <button
                        type="button"
                        className="pro-action-btn success"
                        title="Imprimir"
                        onClick={() =>
                          previsualizarPDF(factura)
                        }
                      >

                        <i className="bi bi-printer-fill"></i>

                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>

    {/* ===================================================== */}
    {/* MOBILE CARDS */}
    {/* ===================================================== */}

    <div className="d-md-none">

      <div className="row">

        {loading ? (

          Array.from({ length: 5 }).map((_, index) => (

            <div className="col-12 mb-3" key={index}>

              <div className="mobile-patient-card">

                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line"></div>

              </div>

            </div>

          ))

        ) : (

          displayedFacturas.map((factura) => (

            <div
              className="col-12 mb-3"
              key={factura.factId}
            >

              <div className="mobile-patient-card">

                <div className="mobile-card-top">

                  <div className="patient-info">

                    <div className="patient-avatar">

                      <i className="bi bi-receipt"></i>

                    </div>

                    <div>

                      <div className="patient-name">

                        {getConceptoNombre(
                          factura.conceptoId
                        )}

                      </div>

                      <div className="patient-id">

                        Factura #{factura.factId}

                      </div>

                    </div>

                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Paciente / Ingreso
                  </small>

                  <div>

                    {factura.patient
                      ? `${factura.patient.pcteNom || ""} ${
                          factura.patient.pcteNom2 || ""
                        } ${factura.patient.pcteApe1 || ""} ${
                          factura.patient.pcteApe2 || ""
                        }`
                      : "OTROS INGRESOS"}

                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Fecha
                  </small>

                  <div>
                    {factura.factFec}
                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Monto
                  </small>

                  <div className="fw-bold text-success">

                    {parseFloat(
                      factura.facturaCt
                    ).toLocaleString("es-DO", {
                      style: "currency",
                      currency: "DOP",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}

                  </div>

                </div>

                <div className="mobile-card-actions">

                  <button
                    type="button"
                    className="pro-action-btn primary"
                    onClick={() => openModal(factura)}
                  >

                    <i className="bi bi-pencil-square"></i>

                  </button>

                  <button
                    type="button"
                    className="pro-action-btn success"
                    onClick={() =>
                      previsualizarPDF(factura)
                    }
                  >

                    <i className="bi bi-printer-fill"></i>

                  </button>

                </div>

              </div>

            </div>

          ))

        )}

      </div>

    </div>

    {/* ===================================================== */}
    {/* PAGINATION */}
    {/* ===================================================== */}

    {!showForm && (

      <div className="pro-pagination">

        <nav aria-label="Page navigation example">

          <ul className="pagination modern-pagination">

            {/* PREVIOUS */}

            <li
              className={`page-item ${
                pageNumber === 0
                  ? "disabled"
                  : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber - 1,
                  })
                }
                style={{
                  cursor:
                    pageNumber === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >

                <i className="bi bi-chevron-left"></i>

              </span>

            </li>

            {/* NUMBERS */}

            {Array.from(
              { length: Math.min(pageCount, 10) },
              (_, index) => (

                <li
                  key={index}
                  className={`page-item ${
                    pageNumber === index
                      ? "active"
                      : ""
                  }`}
                >

                  <span
                    className="page-link"
                    onClick={() =>
                      handlePageChange({
                        selected: index,
                      })
                    }
                  >

                    {index + 1}

                  </span>

                </li>

              )
            )}

            {/* NEXT */}

            <li
              className={`page-item ${
                pageNumber === pageCount - 1
                  ? "disabled"
                  : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber + 1,
                  })
                }
                style={{
                  cursor:
                    pageNumber ===
                    pageCount - 1
                      ? "not-allowed"
                      : "pointer",
                }}
              >

                <i className="bi bi-chevron-right"></i>

              </span>

            </li>

          </ul>

        </nav>

      </div>

    )}

    {/* ===================================================== */}
    {/* MODAL FACTURA */}
    {/* ===================================================== */}

    <Modal
      isOpen={modalOpen}
      toggle={closeModal}
      centered
      modalClassName="pro-modal-xl"
    >
      <ModalHeader toggle={closeModal}>
        {isEditing
          ? "Editar Factura"
          : "Nueva Factura"}
      </ModalHeader>

      <ModalBody>

        <div className="row g-4 align-items-start">

          {/* =============================================== */}
          {/* LEFT */}
          {/* =============================================== */}

          <div className="col-12 col-xl-4">

            <div className="pro-form-card h-100">

              <div className="pro-section-mini-header mb-4">

                <h5>
                  Datos del Paciente
                </h5>

                <span>
                  Información General
                </span>

              </div>

              <Form>

                <FormGroup className="mb-4">

                  <Label className="pro-label">
                    Paciente
                  </Label>

                  <div className="d-flex gap-2">

                    <Input
                      type="text"
                      className="pro-input"
                      value={
                        formulario.nombreDelPaciente ||
                        ""
                      }
                      disabled
                    />

                    <Button
                      onClick={openPacienteModal}
                      className="pro-btn-primary-sm"
                      disabled={isEditing}
                    >

                      <i className="bi bi-search"></i>

                    </Button>

                  </div>

                </FormGroup>

                <FormGroup className="mb-4">

                  <Label className="pro-label">
                    Dirección
                  </Label>

                  <Input
                    type="text"
                    className="pro-input"
                    value={
                      formulario.direccionPaciente
                    }
                    disabled
                  />

                </FormGroup>

                <div className="row">

                  <div className="col-md-6">

                    <FormGroup className="mb-4">

                      <Label className="pro-label">
                        Contacto
                      </Label>

                      <Input
                        type="text"
                        className="pro-input"
                        value={
                          formulario.contactoPaciente
                        }
                        disabled
                      />

                    </FormGroup>

                  </div>

                  <div className="col-md-6">

                    <FormGroup className="mb-4">

                      <Label className="pro-label">
                        Email
                      </Label>

                      <Input
                        type="text"
                        className="pro-input"
                        value={
                          formulario.emailPaciente
                        }
                        disabled
                      />

                    </FormGroup>

                  </div>

                </div>

              </Form>

            </div>

          </div>

          {/* =============================================== */}
          {/* RIGHT */}
          {/* =============================================== */}

          <div className="col-12 col-xl-8">

            <div className="pro-form-card h-100">

              <div className="pro-section-mini-header mb-4">

                <h5>
                  Datos de Facturación
                </h5>

                <span>
                  Completa la información
                </span>

              </div>

              <Form>

                <div className="row">

                  <div className="col-md-6">

                    <FormGroup className="mb-4">

                      <Label className="pro-label">
                        Concepto
                      </Label>

                      <Input
                        type="select"
                        className="pro-input"
                        value={
                          formulario.conceptoId ||
                          ""
                        }
                        onChange={(e) =>
                          setFormulario({
                            ...formulario,
                            conceptoId:
                              e.target.value,
                          })
                        }
                      >

                        <option value="">
                          Seleccione un concepto
                        </option>

                        {conceptos.map((concepto) => (

                          <option
                            key={
                              concepto.conceptoId
                            }
                            value={
                              concepto.conceptoId
                            }
                          >

                            {concepto.conceptoNm}

                          </option>

                        ))}

                      </Input>

                    </FormGroup>

                  </div>

                  <div className="col-md-6">

                    <FormGroup className="mb-4">

                      <Label className="pro-label">
                        Monto
                      </Label>

                      <Input
                        className="pro-input fw-bold text-end"
                        style={{
                          fontSize: "2rem",
                          height: "4.2rem",
                        }}
                        type="text"
                        placeholder="RD$0.00"
                        value={
                          formulario.montoFactura
                        }
                        onChange={handleChange}
                        onFocus={handleFocus}
                      />

                    </FormGroup>

                  </div>

                </div>

                <FormGroup>

  <Label className="pro-label">
    Detalle de Factura
  </Label>

  <div
    className="position-relative"

    onBeforeInputCapture={(e) => {
      const currentLength =
        getPlainTextLength(
          formulario.detalleFactura || ""
        );

      const newText = e.data || "";

      if (currentLength >= maxChars3) {
        e.preventDefault();
        return;
      }

      if (
        currentLength + newText.length >
        maxChars3
      ) {
        e.preventDefault();
      }
    }}

    onPasteCapture={(e) => {
      const currentLength =
        getPlainTextLength(
          formulario.detalleFactura || ""
        );

      const pastedText =
        e.clipboardData?.getData("text") || "";

      if (
        currentLength + pastedText.length >
        maxChars3
      ) {
        e.preventDefault();
      }
    }}
  >

    <ProRichTextEditor
      value={formulario.detalleFactura || ""}
      onChange={(html) => {
        const plainTextLength =
          getPlainTextLength(html);

        if (plainTextLength <= maxChars3) {
          setFormulario((prev) => ({
            ...prev,
            detalleFactura: html,
          }));
        }
      }}
      placeholder="Detalle de Factura..."
    />

    <div className="pro-rich-counter">
      {Math.max(
        0,
        maxChars3 -
          getPlainTextLength(
            formulario.detalleFactura || ""
          )
      )}
      /{maxChars3}
    </div>

  </div>

</FormGroup>

              </Form>

            </div>

          </div>

        </div>

      </ModalBody>

      <ModalFooter>
        <Button
          className="pro-btn-cancel"
          onClick={closeModal}
        >

          Cancelar

        </Button>

        <Button
          className="pro-btn-save"
          onClick={
            isEditing
              ? editarFactura
              : guardarNuevoFactura
          }
        >
          <i className="bi bi-floppy-fill me-2"></i>
          {isEditing
            ? "Guardar Cambios"
            : "Guardar Factura"}
        </Button>

      </ModalFooter>

    </Modal>

    {/* ===================================================== */}
    {/* MODAL PACIENTES */}
    {/* ===================================================== */}

    <Modal
      isOpen={isPacienteModalOpen}
      toggle={closePacienteModal}
      centered
      modalClassName="pro-modal-lg"
    >

      <ModalHeader toggle={closePacienteModal}>

        Seleccionar Paciente

      </ModalHeader>

      <ModalBody>

        {/* SEARCH */}

        <div className="pro-search-modal mb-4">

          <i className="bi bi-search"></i>

          <input
            type="text"
            placeholder="Buscar paciente..."
            value={modalSearchTerm}
            onChange={handleModalSearchTermChange}
            ref={searchInputRef}
          />

        </div>

        {/* LIST */}

        <div className="pro-patient-list">

          {filteredPacientes
            .slice(
              (currentPage - 1) * itemsPerPage,
              currentPage * itemsPerPage
            )
            .map((paciente) => {

              const fullName = [
                paciente.pcteNom,
                paciente.pcteNom2,
                paciente.pcteApe1,
                paciente.pcteApe2,
              ]
                .filter(Boolean)
                .join(" ");

              return (

                <div
                  key={paciente.pcteId}
                  className="pro-patient-item"
                >

                  <div className="patient-info">

                    <div className="patient-avatar">

                      {paciente.pcteNom?.charAt(0)}

                    </div>

                    <div>

                      <div className="patient-name">

                        {fullName}

                      </div>

                      <div className="patient-id">

                        Paciente #
                        {paciente.pcteId}

                      </div>

                    </div>

                  </div>

                  <button
                    className="pro-btn-primary-sm"
                    onClick={() =>
                      handlePacienteSelection(
                        paciente
                      )
                    }
                  >

                    Seleccionar

                  </button>

                </div>

              );

            })}

        </div>

        {/* PAGINATION */}

        <div className="pro-pagination mt-4">

          <nav>

            <ul className="pagination modern-pagination">

              <li className="page-item">

                <span
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(1)
                  }
                >

                  <i className="bi bi-chevron-double-left"></i>

                </span>

              </li>

              <li className="page-item">

                <span
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(
                      Math.max(
                        1,
                        currentPage - 10
                      )
                    )
                  }
                >

                  <i className="bi bi-chevron-left"></i>

                </span>

              </li>

              {Array.from(
                {
                  length: Math.min(
                    Math.ceil(
                      filteredPacientes.length /
                        itemsPerPage
                    ),
                    10
                  ),
                },
                (_, index) => (

                  <li
                    key={index}
                    className={`page-item ${
                      currentPage ===
                      index + 1
                        ? "active"
                        : ""
                    }`}
                  >

                    <span
                      className="page-link"
                      onClick={() =>
                        setCurrentPage(
                          index + 1
                        )
                      }
                    >

                      {index + 1}

                    </span>

                  </li>

                )
              )}

              <li className="page-item">

                <span
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(
                      Math.min(
                        Math.ceil(
                          filteredPacientes.length /
                            itemsPerPage
                        ),
                        currentPage + 10
                      )
                    )
                  }
                >

                  <i className="bi bi-chevron-right"></i>

                </span>

              </li>

            </ul>

          </nav>

        </div>

      </ModalBody>

    </Modal>

  </div>
);
};
export default FacturaForm;
